import dayjs from 'dayjs'
import type { Direction, ExportState, FuelType, MovementStatus, VehicleOwnership } from '@/api/types'

export function formatMoney(amount: number | null, currency: string | null, direction?: Direction | null): string {
  if (amount === null) return '—'
  const signed = direction === 'OUT' ? -Math.abs(amount) : amount
  return `${signed.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${currency ?? ''}`.trim()
}

export function formatDate(iso: string | null): string {
  return iso ? dayjs(iso).format('D. M. YYYY') : '—'
}

export function formatDateTime(iso: string): string {
  return dayjs(iso).format('D. M. YYYY HH:mm')
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toLocaleString('cs-CZ', { maximumFractionDigits: 0 })} kB`
  return `${(bytes / 1024 / 1024).toLocaleString('cs-CZ', { maximumFractionDigits: 1 })} MB`
}

export const STATUS_LABELS: Record<MovementStatus, string> = {
  MISSING: 'Chybí doklad',
  SUPPLIED: 'Doloženo',
  NOT_NEEDED: 'Nepotřebuje doklad',
  UNTRACEABLE: 'Nelze dohledat',
}

export const STATUS_STYLES: Record<MovementStatus, string> = {
  MISSING: 'bg-red-100 text-red-700',
  SUPPLIED: 'bg-green-100 text-green-700',
  NOT_NEEDED: 'bg-slate-200 text-slate-600',
  UNTRACEABLE: 'bg-amber-100 text-amber-800',
}

/** Štítek exportu do Digitoo; NONE a NEW se neukazují. */
export const EXPORT_LABELS: Partial<Record<ExportState, string>> = {
  EXPORTED: 'Exportováno',
  PENDING: 'Odesílá se…',
  FAILED: 'Neodešlo',
}

export const EXPORT_STYLES: Partial<Record<ExportState, string>> = {
  EXPORTED: 'bg-sky-100 text-sky-700',
  PENDING: 'bg-violet-100 text-violet-700',
  FAILED: 'bg-red-600 text-white',
}

export const FUEL_LABELS: Record<FuelType, string> = {
  PETROL_95: 'Benzin 95',
  PETROL_98: 'Benzin 98',
  DIESEL: 'Nafta',
  ELECTRIC: 'Elektřina',
  LPG: 'LPG',
  CNG: 'CNG',
}

export const OWNERSHIP_LABELS: Record<VehicleOwnership, string> = {
  COMPANY: 'Firemní',
  EMPLOYEE: 'Soukromé (zaměstnanec)',
}

export function formatNumber(n: number | null, digits = 2): string {
  return n === null ? '—' : n.toLocaleString('cs-CZ', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

/** "1AB2345" → "1AB 2345" — jen pro čtení, ukládá se bez mezery. */
export function formatPlate(plate: string): string {
  return plate.length === 7 ? `${plate.slice(0, 3)} ${plate.slice(3)}` : plate
}

export function formatKm(km: number | null): string {
  return km === null ? '—' : `${km.toLocaleString('cs-CZ')} km`
}

export function formatCzk(n: number | null): string {
  return n === null ? '—' : `${formatNumber(n)} Kč`
}
